import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { subscriptionTranslations } from '../i18n/translations';

const Abonnement = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, subscriptionTranslations);
  /* =========================
     OFFRES TOURNOIS
  ========================= */
  const plans = useMemo(
    () => [
      {
        id: "starter",
  
        name: "CHILL",
        price: "95€",
        description: t('chillDescription'),
        features: t('chillFeatures').split('|'),
      },
      {
        id: "pro",
  
        name: "INTENSIVE",
        price: "180€",
        description: t('intensiveDescription'),
        features: t('intensiveFeatures').split('|'),
      },
      {
        id: "premium",
  
        name: "INTENSIVE+",
        price: "350€",
        description: t('plusDescription'),
        features: t('plusFeatures').split('|'),
      },
    ],
    [t]
  );

  /* =========================
     STATE FORM
  ========================= */
  const [form, setForm] = useState({
    requestType: "abonnement",
    fullName: "",
    email: "",
    phone: "",
    club: "",
    plan: "pro",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onSelectPlan = (planId) => {
    setForm((prev) => ({
      ...prev,
      requestType: "abonnement",
      plan: planId,
    }));
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  /* =========================
     SUBMIT → FIREBASE FUNCTION
  ========================= */
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", msg: "" });

    try {
      const response = await fetch(
        "https://us-central1-mendyx-adafe.cloudfunctions.net/sendSubscriptionMail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, language }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Erreur lors de l’envoi");
      }

      setStatus({
        type: "success",
        msg: t('sent'),
      });

      setForm({
        requestType: "abonnement",
        fullName: "",
        email: "",
        phone: "",
        club: "",
        plan: "pro",
        message: "",
      });
    } catch (error) {
      console.error("Erreur envoi formulaire :", error);
      setStatus({
        type: "error",
        msg: t('error'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('pageTitle')} | Padel Mendyx</title>
        <meta
          name="description"
          content="Abonnements tournois Padel Mendyx – choisis ton pack et contacte-nous."
        />
      </Helmet>

      <section className="bg-white min-h-screen">
        <div className="container mx-auto px-4 py-14">

          {/* HERO */}
          <div className="text-center max-w-4xl mx-auto">
            <img
              src="/logo2.jfif"
              alt="Padel Mendyx"
              className="mx-auto h-28 w-28 rounded-full shadow mb-6"
            />
            <h1 className="text-3xl md:text-4xl font-semibold text-[#4c2f8e]">
              {t('hero')}
            </h1>
            <p className="mt-3 text-gray-600">
              {t('intro')}
            </p>
          </div>

          {/* PLANS */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {plans.map((p) => (
              <div
                key={p.id}
                className="rounded-[32px] bg-white shadow-lg ring-1 ring-black/5 p-8 relative"
              >
                <span className="absolute top-5 left-5 text-xs font-semibold bg-[#4c2f8e] text-white px-3 py-1 rounded-full">
                  {p.label}
                </span>

                <h2 className="text-2xl font-semibold text-[#4c2f8e] mt-10">
                  {p.name}
                </h2>

                <div className="mt-2 text-4xl font-semibold">{p.price}</div>
                <p className="mt-3 text-sm text-gray-600">{p.description}</p>

                <ul className="mt-6 space-y-3 text-sm">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-2 h-2 w-2 rounded-full bg-[#ea4c89]" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => onSelectPlan(p.id)}
                  className="mt-8 w-full rounded-full bg-[#4c2f8e] text-white py-3 hover:bg-[#ea4c89]"
                >
                  {t('choose')}
                </button>
              </div>
            ))}
          </div>

          {/* FORMULAIRE */}
          <div className="mt-16 max-w-4xl mx-auto bg-white shadow-lg rounded-[32px] p-10">
            <h3 className="text-2xl font-semibold text-[#4c2f8e] mb-6">
              {t('formTitle')}
            </h3>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <select
                name="requestType"
                value={form.requestType}
                onChange={onChange}
                className="md:col-span-2 rounded-2xl border px-4 py-3"
              >
                <option value="abonnement">{t('subscriptionRequest')}</option>
                <option value="sponsoring">{t('sponsorRequest')}</option>
              </select>

              <input
                name="fullName"
                required
                placeholder={t('fullName')}
                value={form.fullName}
                onChange={onChange}
                className="rounded-2xl border px-4 py-3"
              />

              <input
                type="email"
                name="email"
                required
                placeholder={t('email')}
                value={form.email}
                onChange={onChange}
                className="rounded-2xl border px-4 py-3"
              />

              <input
                name="phone"
                placeholder={t('phone')}
                value={form.phone}
                onChange={onChange}
                className="rounded-2xl border px-4 py-3"
              />

              <input
                name="club"
                placeholder={t('club')}
                value={form.club}
                onChange={onChange}
                className="rounded-2xl border px-4 py-3"
              />

              {form.requestType === "abonnement" && (
                <select
                  name="plan"
                  value={form.plan}
                  onChange={onChange}
                  className="md:col-span-2 rounded-2xl border px-4 py-3"
                >
                  <option value="starter">CHILL</option>
                  <option value="pro">INTENSIVE</option>
                  <option value="premium">INTENSIVE+</option>
                </select>
              )}

              <textarea
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={onChange}
                className="md:col-span-2 rounded-2xl border px-4 py-3"
                placeholder={t('explain')}
              />

              {status.msg && (
                <p
                  className={`md:col-span-2 text-sm ${
                    status.type === "success"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {status.msg}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="md:col-span-2 rounded-full bg-[#4c2f8e] text-white py-3 hover:bg-[#ea4c89]"
              >
                {loading ? t('sending') : t('send')}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default Abonnement;
